import { OFFICE_APP } from './paneHost.js';
/**
 * Why the desktop's Windows recognizer stopped, as the agent reports it (Gyro.Core DictationReasons), in plain words for
 * the notice (owner, 2026-10-21: the generic "Voice input stopped" said nothing about what had happened). Undefined for a
 * reason the widget does not know, which leaves the code's own sentence.
 */
export function dictationStoppedBecause(reason, seconds) {
    const time = typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0
        ? (seconds >= 120 ? `${Math.round(seconds / 60)} minutes` : `${Math.round(seconds)} seconds`)
        : undefined;
    switch (reason) {
        case 'silence': return `no speech was heard${time ? ` for ${time}` : ''}`;
        case 'processing': return 'Windows online speech recognition took too long to work out what you said';
        case 'network': return 'the Windows speech service could not be reached';
        case 'audio': return 'Windows could not use the microphone audio';
        case 'cancelled': return 'Windows cancelled the speech recognition session';
        case 'unknown': return 'Windows speech recognition stopped without giving a reason';
        case 'not-available': return 'Windows speech recognition is not available';
        case 'limit': return `one dictation can run for ${time ?? '5 minutes'} at most`;
        case 'stop-failed': return 'Windows speech recognition did not finish the session';
        case 'start-failed': return 'the speech recognizer could not start';
        default: return undefined;
    }
}
export function speechInputError(error, reason, seconds) {
    const because = dictationStoppedBecause(reason, seconds);
    if (because) {
        const kept = error === 'no-speech' ? ' Check your microphone and try again.' : ' Your draft has been kept.';
        return `Voice input stopped: ${because}.${kept}`;
    }
    switch (error) {
        case 'unsupported': return 'Voice input is unavailable in this browser or desktop runtime. You can still type your message.';
        case 'not-allowed':
        case 'service-not-allowed': return 'Microphone or speech access was denied. Check Windows microphone privacy settings and your browser or organization permissions, then try again.';
        case 'audio-capture': return 'No microphone could be opened. Connect a microphone and check your Windows input device, then try again.';
        case 'network': return 'The speech service could not be reached. Check your connection or desktop runtime and try again. Your draft has been kept.';
        case 'no-speech': return 'No speech was detected. Check your microphone and try again.';
        case 'aborted': return 'Voice input stopped. Your draft has been kept.';
        case 'language-not-supported': return 'English voice input is unavailable in this browser or desktop runtime. You can still type your message.';
        case 'start-timeout': return 'The microphone did not start. Check microphone permissions and your input device, then try again.';
        case 'stop-timeout': return 'The speech service did not finish transcribing. Your captured words are kept in the draft for you to review.';
        case 'windows-recognizer-missing': return 'This PC has no English (United States) speech recognizer for Windows dictation, so voice input is unavailable. Ask IT to add the English speech pack in Windows Settings > Time & language > Speech. You can still type your message.';
        default: return 'Voice input could not start or was interrupted. Check your microphone and try again. Your draft has been kept.';
    }
}
/**
 * The Gyro desktop shell (WebView2). Its dictation must stay on the local Windows recognizer: WebView2's own
 * SpeechRecognition is a browser (cloud) service, so the shell never falls back to it. Only the shell's own flag counts
 * (shell.html sets `window.__gyroDesktopShell = true` before the widget loads), not `window.chrome.webview`: Office on
 * Windows hosts its task panes in WebView2 too, and an Office pane must keep browser dictation (contract §5).
 */
export function isDesktopShell(win) {
    return Boolean(win && typeof win === 'object' && win.__gyroDesktopShell === true);
}
/**
 * The Office add-in's task pane (contract §5), as apps/office-addin/src/taskpane/App.tsx mounts it: the full entry, which
 * hosts a pane's own tools and actions (`paneHost`; the web entry never does), with `layout: 'pane'` and the app 'office'.
 * A pane for any other app (the custom element's `layout="pane"` on a web page, say) is a web page like any other.
 */
export function isOfficePane(host) {
    return host.paneHost && host.layout === 'pane' && host.appKey === OFFICE_APP;
}
export function voiceInputFor(host) {
    if (host.desktopShell)
        return 'windows';
    return host.officePane ? 'browser' : 'off';
}
/** What a web page says when something asks it for voice input (voiceInputFor 'off'). */
export const VOICE_INPUT_OFF = 'Voice input is not available here. You can still type your message.';
/**
 * How long a stopped dictation waits for the recognizer's final text before it gives up with 'stop-timeout' (the words
 * heard so far stay in the draft). A browser's own recognizer answers within 5 seconds. The desktop's Windows online
 * speech recognition returns the final text of a long phrase from Microsoft's service after the stop, often later than
 * 5 seconds, so the desktop waits up to 20 (owner report 2026-10-12); the agent's own finishing cap
 * (SpotlightOverlay.Dictation.cs DictationFinishingCapMs, 22 seconds) stays above it.
 */
export const BROWSER_DICTATION_STOP_TIMEOUT_MS = 5_000;
export const NATIVE_DICTATION_STOP_TIMEOUT_MS = 20_000;
/** Browser dictation lifecycle. Text is drafted for review, never automatically sent. */
export class SpeechInput {
    options;
    active = false;
    stopping = false;
    recognition = null;
    heard = '';
    startTimer;
    stopTimer;
    schedule;
    unschedule;
    constructor(options) {
        this.options = options;
        this.schedule = options.schedule ?? ((callback, delayMs) => setTimeout(callback, delayMs));
        this.unschedule = options.unschedule ?? (timer => clearTimeout(timer));
    }
    get isActive() { return this.active; }
    start() {
        if (this.active)
            return;
        this.options.onError('');
        if (!this.options.createRecognition) {
            this.options.onError(speechInputError(this.options.unsupportedError ?? 'unsupported'));
            this.options.onState('idle');
            return;
        }
        this.active = true;
        this.stopping = false;
        this.heard = '';
        this.options.onState('starting');
        try {
            const recognition = this.options.createRecognition();
            this.recognition = recognition;
            recognition.lang = 'en-US';
            recognition.interimResults = true;
            recognition.continuous = true;
            recognition.maxAlternatives = 1;
            recognition.onstart = () => {
                if (!this.current(recognition))
                    return;
                this.clearStartTimer();
                if (!this.stopping)
                    this.options.onState('listening');
            };
            recognition.onresult = event => {
                if (!this.current(recognition))
                    return;
                this.heard = Array.from(event.results ?? [], result => result[0]?.transcript ?? '').join(' ').trim();
                this.options.onDraft(this.draft());
            };
            recognition.onaudiolevel = event => {
                if (this.current(recognition) && !this.stopping && Number.isFinite(event.level))
                    this.options.onAudioLevel?.(Math.max(0, Math.min(1, event.level)));
            };
            recognition.onerror = event => {
                if (this.current(recognition))
                    this.fail(event.error, event.reason, event.seconds);
            };
            recognition.onend = event => {
                if (!this.current(recognition))
                    return;
                if (this.heard) {
                    // The words are kept; a session that ended by itself (the desktop's silence limit) still says why.
                    this.finish(true);
                    const because = dictationStoppedBecause(event?.reason, event?.seconds);
                    if (because)
                        this.options.onError(`Voice input stopped: ${because}. Your words are kept in the draft.`);
                }
                else
                    this.fail('no-speech', event?.reason, event?.seconds);
            };
            this.startTimer = this.schedule(() => {
                if (this.current(recognition))
                    this.fail('start-timeout');
            }, 10_000);
            recognition.start();
        }
        catch (error) {
            const name = error instanceof Error ? error.name : '';
            this.fail(name === 'NotAllowedError' || name === 'SecurityError' ? 'not-allowed' : undefined);
        }
    }
    /**
     * Request final speech results; keep accepting them until end or the bounded timeout. A final result that arrives
     * meanwhile replaces the interim text (onresult), and the person can still cancel.
     */
    stop() {
        if (!this.current() || this.stopping)
            return;
        this.stopping = true;
        this.clearStartTimer();
        this.options.onState('stopping');
        const recognition = this.recognition;
        this.stopTimer = this.schedule(() => {
            if (this.current(recognition))
                this.fail('stop-timeout');
        }, this.stopTimeoutMs());
        try {
            recognition.stop();
        }
        catch {
            this.fail();
        }
    }
    /** Discard this recording only; never restore its old draft into a different chat/identity. */
    cancel() {
        if (!this.active)
            return;
        const restoreDraft = this.options.isCurrent?.() !== false;
        this.finish(false);
        if (restoreDraft)
            this.options.onDraft(this.options.initialText ?? '');
    }
    stopTimeoutMs() {
        const requested = this.options.stopTimeoutMs;
        return typeof requested === 'number' && Number.isFinite(requested) && requested > 0
            ? Math.min(requested, NATIVE_DICTATION_STOP_TIMEOUT_MS)
            : BROWSER_DICTATION_STOP_TIMEOUT_MS;
    }
    current(recognition) {
        if (!this.active)
            return false;
        if (recognition && recognition !== this.recognition)
            return false;
        if (this.options.isCurrent?.() === false) {
            this.cancel();
            return false;
        }
        return true;
    }
    draft() {
        const initialText = this.options.initialText ?? '';
        return this.heard ? `${initialText}${initialText && !/\s$/.test(initialText) ? ' ' : ''}${this.heard}` : initialText;
    }
    clearStartTimer() {
        if (this.startTimer !== undefined)
            this.unschedule(this.startTimer);
        this.startTimer = undefined;
    }
    fail(error, reason, seconds) {
        this.finish(false);
        this.options.onError(speechInputError(error, reason, seconds));
    }
    finish(complete) {
        this.active = false;
        this.stopping = false;
        this.clearStartTimer();
        if (this.stopTimer !== undefined)
            this.unschedule(this.stopTimer);
        this.stopTimer = undefined;
        const recognition = this.recognition;
        this.recognition = null;
        if (recognition) {
            recognition.onstart = recognition.onresult = recognition.onerror = recognition.onend = null;
            recognition.onaudiolevel = null;
            try {
                if (recognition.abort)
                    recognition.abort();
                else
                    recognition.stop();
            }
            catch { /* Already stopped. */ }
        }
        this.options.onState('idle');
        if (complete && this.heard)
            this.options.onComplete(this.draft());
    }
}
//# sourceMappingURL=speechInput.js.map